import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Gallery, GalleryImage } from '@/domain/types/collections';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import GalleryImagesUpload from '@/presentation/components/gallery-images-upload';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useState } from 'react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const GalleryAddPage = () => {
  const navigate = useNavigate();
  const { logCreate } = useAdminLogger();

  const [formData, setFormData] = useState<Omit<Gallery, 'id' | 'createdAt'>>({
    title: '',
    titleEN: '',
    images: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImagesChange = (images: GalleryImage[]) => {
    setFormData((prev) => ({ ...prev, images }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('El título en español es requerido', { theme: 'colored' });
      return;
    }

    if (!formData.titleEN.trim()) {
      toast.error('El título en inglés es requerido', { theme: 'colored' });
      return;
    }

    if (formData.images.length === 0) {
      toast.error('Debe agregar al menos una imagen', { theme: 'colored' });
      return;
    }

    setIsSubmitting(true);

    try {
      const docRef = await addDoc(collection(db, DOCUMENTS_DB.GALLERIES), {
        ...formData,
        createdAt: serverTimestamp(),
      });

      // Log the action
      await logCreate(
        DOCUMENTS_DB.GALLERIES,
        docRef.id,
        formData.title,
        formData,
      );

      toast.success('Galería creada correctamente', { theme: 'colored' });
      navigate('/dashboard/galleries');
    } catch (error) {
      console.error('Error creating gallery:', error);
      toast.error('Error al crear la galería', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/galleries')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Agregar Galería</h1>
        </div>

        <button
          onClick={handleSave}
          disabled={isSubmitting}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiSave />
          {isSubmitting ? 'Guardando...' : 'Guardar Galería'}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Títulos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título (Español) *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Ingrese el título en español"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (English) *
            </label>
            <input
              type="text"
              name="titleEN"
              value={formData.titleEN}
              onChange={handleInputChange}
              placeholder="Enter title in English"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Imágenes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Imágenes de la Galería *
          </label>
          <GalleryImagesUpload
            images={formData.images}
            onChange={handleImagesChange}
          />
        </div>
      </div>
    </div>
  );
};

export default GalleryAddPage;
